import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SchematicsModel } from "../model/calculations/schematics";
import { CaseComparison } from "../model/calculations/calculation";

interface CalculationState {
  loading: boolean;
  error: string | undefined;
  projectDetails: { [key: string]: any };
  schematicData: SchematicsModel | null;
  surfaceVoltage: number;
  motorLoadFrequency: any;
  motorFrequencyDataForSchematics: any;
  setDepthTypeValue: number;
  caseModifiedDate: string;
  aiEnabled: boolean;
  intakeMD?: number;
  schematicLoading: boolean;
  setDepthLoading: boolean;
}

export const initialState: CalculationState = {
  loading: false,
  error: "",
  projectDetails: {},
  schematicData: null,
  surfaceVoltage: 0,
  motorLoadFrequency: null,
  motorFrequencyDataForSchematics: null,
  setDepthTypeValue: 0,
  caseModifiedDate: "",
  aiEnabled: false,
  intakeMD: 0,
  schematicLoading: false,
  setDepthLoading: false,
};

export const fetchTemplateCalculations = createAction(
  "calculations/fetchTemplateCalculations"
);
export const fetchSchematicData = createAction(
  "calculations/fetchSchematicData"
);
export const fetchSetDepth = createAction("calculations/fetchSetDepth");
export const fetchFormattedSchematicData = createAction(
  "calculations/fetchFormattedSchematicData"
);
export const fetchUpdatedSchematicEquipmentData = createAction(
  "calculations/fetchUpdatedSchematicEquipmentData"
);
export const calculateMotorLoadDifferentFrequency = createAction<{
  payload: any;
}>("calculations/calculateMotorLoadDifferentFrequency");
export const calculateMotorLoadFrequency = createAction(
  "calculations/calculateMotorLoadFrequency"
);
export const saveMainReportData = createAction(
  "calculations/saveMainReportData"
);
export const saveWellReportData = createAction(
  "calculations/saveWellReportData"
);
export const saveProjectData = createAction("calculations/saveProjectData");
export const saveSchematicData = createAction("calculations/saveSchematicData");
export const saveMainReportDataLake = createAction(
  "calculations/saveMainReportDataLake"
);
export const emptyaction = createAction("calculations/emptyaction");

export const calculateFailure = (error: any) => ({
  type: "CALCULATE_FAILURE",
  error,
});

const CalculationSlice = createSlice({
  name: "calculations",
  initialState,
  reducers: {
    setProjectDetails(state, action) {
      state.projectDetails = action.payload;
      state.error = "";
    },
    setProjectStatus(state, action) {
      state.projectDetails = {
        ...state.projectDetails,
        status: action.payload,
      };
    },
    setSchematicData: (state, action) => {
      state.schematicData = action?.payload;
    },
     setCaseModifiedDate: (state, action) => {
      state.caseModifiedDate = action?.payload;
    },
     setAIEnabled: (state, action) => {
      state.aiEnabled = action?.payload;
    },
    setSchematicEquipmentData: (state, action) => {
      if (state.schematicData) {
        state.schematicData = {
          ...state.schematicData,
          equipmentsList:
            action?.payload?.length > 0
              ? action?.payload?.filter(
                  (item: any) => Object?.keys?.(item)?.length > 0
                )
              : [],
        };
      }
    },
    setSchematicSetDepthTypeValue: (state: any, action) => {
      state.schematicData = {
        ...state.schematicData,
        miscellaneous: {
          ...state.schematicData?.miscellaneous,
          setDepthType: action?.payload ?? 0,
        },
      };
    },
    setSchematicOtherData: (state, action) => {
      if (state.schematicData) {
        state.schematicData = {
          ...state.schematicData,
          miscellaneous: {
            ...state.schematicData.miscellaneous,
            ...action?.payload,
          },
        };
      }
    },
    setSchematicMiscSetDepthValue: (state, action) => {
      if (state.schematicData) {
        state.schematicData = {
          ...state.schematicData,
          miscellaneous: {
            ...state.schematicData.miscellaneous,
            setDepthValue: action?.payload,
          },
        };
      }
    },
    setSurfaceVoltage: (state, action) => {
      state.surfaceVoltage = action?.payload;
    },
    setMotorLoadFrequencyData: (state, action) => {
      state.motorFrequencyDataForSchematics = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSchematicLoading(state, action: PayloadAction<boolean>) {
      state.schematicLoading = action.payload;
    },
    setSetDepthLoading(state, action: PayloadAction<boolean>) {
      state.setDepthLoading = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    fetchProjectDetailsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setIntakeMD(state, action: PayloadAction<number>) {
      state.intakeMD = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("CALCULATE_FAILURE", (state, action: any) => {
      state.loading = false;
      state.error = action.error?.message || "Calculate failed";
    });
  },
});

export const {
  setProjectDetails,
  setSchematicData,
  setSchematicEquipmentData,
  setSchematicMiscSetDepthValue,
  setSchematicSetDepthTypeValue,
  setSurfaceVoltage,
  setMotorLoadFrequencyData,
  setSchematicOtherData,
  setLoading,
  setSchematicLoading,
  setSetDepthLoading,
  setError,
  setProjectStatus,
  setCaseModifiedDate,
  setAIEnabled,
  setIntakeMD,
} = CalculationSlice.actions;
export default CalculationSlice.reducer;
